'use client'

import React from 'react'
import { useLanguage } from '@/lib/i18n/language-provider'
import type { WorkspaceElement, LEDProcessor, LEDPanel, PowerCabinet, WorkflowStep } from '@/types/synoptique'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  ChevronDown,
  Cpu,
  Database,
  Link as LinkIcon,
  Monitor,
  RotateCcw,
  Shield,
  Zap,
  Settings,
} from 'lucide-react'
import { toast } from 'sonner'
import { calculatePortCapacity, calculateMaxPanelsPerPort, type PortCapacityConfig } from '../constants'

interface PanelConnection {
  panelId: string
  processorId: string
  port: number
  [key: string]: unknown
}

interface PowerConnection {
  cabinetId: string
  outlet: number
  [key: string]: unknown
}

interface MireConnection {
  processorId: string
  port: number
  [key: string]: unknown
}

interface CabinetLinkCable {
  id: string
  from: string
  to: string
  color: string
}

interface BottomPanelProps {
  currentStep: WorkflowStep
  isBottomPanelOpen: boolean
  setIsBottomPanelOpen: (v: boolean) => void
  readOnly: boolean
  workspaceElements: WorkspaceElement[]
  panelConnections: PanelConnection[]
  mireConnections: MireConnection[]
  powerConnections: PowerConnection[]
  // Data panel - processor selection
  selectedProcessorForConnection: string | null
  setSelectedProcessorForConnection: (v: string | null) => void
  selectedPort: number | null
  setSelectedPort: (v: number | null) => void
  // Cabling panel - cabinet selection
  selectedCabinetForConnection: string | null
  setSelectedCabinetForConnection: (v: string | null) => void
  selectedOutlet: number | null
  setSelectedOutlet: (v: number | null) => void
  // Liaisons armoire → armoire
  cabinetLinkMode: boolean
  setCabinetLinkMode: (v: boolean) => void
  cabinetLinkCables: CabinetLinkCable[]
  removeCabinetLink: (cableId: string) => void
  cabinetLoadDirect: Map<string, number>
  cabinetLoadTotal: Map<string, number>
  // Processor backup helpers
  isProcessorBackup: (id: string) => boolean
  getBackupProcessor: (id: string) => string | null
  getPrimaryProcessor: (id: string) => string | null
  removeBackupProcessor: (id: string) => void
  // Port helpers
  isPortBackup: (port: number, processorId?: string) => boolean
  isPortPrimary: (port: number, processorId?: string) => boolean
  getBackupForPort: (port: number, processorId?: string) => number | null
  getBackupsForPort: (port: number, processorId?: string) => number[]
  getPrimaryForBackup: (port: number, processorId: string) => number | null
  getPortColor: (port: number, processorId?: string) => string
  getOutletColor: (outlet: number, cabinetId?: string) => string
  // Port drag
  draggedPort: number | null
  setDraggedPort: (v: number | null) => void
  portDragTarget: number | null
  setPortDragMousePosition: (v: { x: number; y: number } | null) => void
  // Links visibility
  showProcessorToFirstPanelLinks: boolean
  setShowProcessorToFirstPanelLinks: (v: boolean) => void
  showCabinetToFirstPanelLinks: boolean
  setShowCabinetToFirstPanelLinks: (v: boolean) => void
  // Backup ports
  setBackupPorts: (fn: (prev: Record<string, Record<number, number>>) => Record<string, Record<number, number>>) => void
  setSkipNextHistorySave: (v: boolean) => void
  saveToHistoryImmediate: (description: string) => void
  // Backup processor drag
  setProcessorForBackup: (v: string | null) => void
  setIsDraggingBackupButton: (v: boolean) => void
  setBackupButtonDragMousePosition: (v: { x: number; y: number } | null) => void
  // Onboarding
  onboarding: { state: { isActive: boolean }; completeAction: (action: string) => void }
  // Constants
  MAX_DAISY_CHAIN_PANELS: number
  // Port capacity configuration
  portCapacityConfig: PortCapacityConfig
  setPortCapacityConfig: (config: PortCapacityConfig) => void
  // Port color overrides
  portColorOverrides: Record<string, Record<number, string>>
  setPortColorOverrides: (fn: (prev: Record<string, Record<number, string>>) => Record<string, Record<number, string>>) => void
}

export function BottomPanel({
  currentStep,
  isBottomPanelOpen,
  setIsBottomPanelOpen,
  readOnly,
  workspaceElements,
  panelConnections,
  mireConnections,
  powerConnections,
  selectedProcessorForConnection,
  setSelectedProcessorForConnection,
  selectedPort,
  setSelectedPort,
  selectedCabinetForConnection,
  setSelectedCabinetForConnection,
  selectedOutlet,
  setSelectedOutlet,
  cabinetLinkMode,
  setCabinetLinkMode,
  cabinetLinkCables,
  removeCabinetLink,
  cabinetLoadDirect,
  cabinetLoadTotal,
  isProcessorBackup,
  getBackupProcessor,
  getPrimaryProcessor,
  removeBackupProcessor,
  isPortBackup,
  isPortPrimary,
  getBackupForPort,
  getBackupsForPort,
  getPrimaryForBackup,
  getPortColor,
  getOutletColor,
  draggedPort,
  setDraggedPort,
  portDragTarget,
  setPortDragMousePosition,
  showProcessorToFirstPanelLinks,
  setShowProcessorToFirstPanelLinks,
  showCabinetToFirstPanelLinks,
  setShowCabinetToFirstPanelLinks,
  setBackupPorts,
  setSkipNextHistorySave,
  saveToHistoryImmediate,
  setProcessorForBackup,
  setIsDraggingBackupButton,
  setBackupButtonDragMousePosition,
  onboarding,
  MAX_DAISY_CHAIN_PANELS,
  portCapacityConfig,
  setPortCapacityConfig,
  portColorOverrides,
  setPortColorOverrides,
}: BottomPanelProps) {
  const { t } = useLanguage()

  return (
    <>
      {/* Menu en bas pour Processeurs & Ports Management */}
      {currentStep === 'data' && isBottomPanelOpen && (
        <div
          className="h-40 bg-background border-t border-border flex items-start px-4 space-x-6 overflow-hidden py-4 relative"
          onClick={(e) => {
            if (readOnly) return
            // Désélectionner le port si on clique sur le bandeau (mais pas sur les boutons ou éléments interactifs)
            const target = e.target as HTMLElement
            const isClickOnButton = target.closest('button')
            const isClickOnInteractive = target.closest('[data-no-deselect]')

            if (!isClickOnButton && !isClickOnInteractive) {
              setSelectedPort(null)
            }
          }}
        >
          {/* Bouton pour masquer le bandeau du bas */}
          <div
            className="absolute top-2 right-4 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors flex items-center gap-1 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation()
              setIsBottomPanelOpen(false)
            }}
            title={t('synoptic.hideBottomPanel')}
          >
            <ChevronDown className="h-4 w-4" />
            <span className="text-xs">{t('synoptic.hide')}</span>
          </div>
          <div className="flex flex-col items-start space-y-2 min-w-0 overflow-hidden">
            <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">{t('synoptic.processorsCount')}</span>
            <div className="flex items-center space-x-2 overflow-hidden">
              {workspaceElements
                .filter(element => element.type === 'processor')
                .map(processor => {
                  const isBackup = isProcessorBackup(processor.id)
                  const backupProcessorId = getBackupProcessor(processor.id)
                  
                  return (
                    <Button
                      key={processor.id}
                      variant={selectedProcessorForConnection === processor.id ? "default" : "outline"}
                      size="sm"
                      className={`h-24 w-20 flex flex-col items-center justify-center p-1 relative ${isBackup ? 'border-destructive border-2' : ''}`}
                      onClick={() => {
                        if (selectedProcessorForConnection === processor.id) {
                          setSelectedProcessorForConnection(null)
                          setSelectedPort(null)
                        } else {
                          setSelectedProcessorForConnection(processor.id)
                          setSelectedPort(null)
                        }
                      }}
                    >
                      <Cpu className="h-4 w-4 mb-1 flex-shrink-0" />
                      <span className="text-xs font-medium text-center w-full leading-tight break-words whitespace-normal overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } as React.CSSProperties}>{processor.data.name}</span>
                      {isBackup && (
                        <div className="absolute top-0 right-0 w-3 h-3 bg-destructive rounded-full border border-background" title="Processeur backup" />
                      )}
                      {backupProcessorId && (
                        <div className="absolute top-0 left-0 w-3 h-3 bg-primary rounded-full border border-background" title="A un processeur backup" />
                      )}
                    </Button>
                  )
                })}
              {workspaceElements.filter(element => element.type === 'processor').length === 0 && (
                <span className="text-xs text-muted-foreground italic">{t('synoptic.noProcessor')}</span>
              )}
            </div>
          </div>


          {selectedProcessorForConnection && workspaceElements.find(e => e.id === selectedProcessorForConnection) && (
            <>
              <Separator orientation="vertical" className="h-24" />
              <div className="flex flex-col items-start min-w-0 overflow-hidden">
                <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">{t('synoptic.portsLabel')}</span>
                {isProcessorBackup(selectedProcessorForConnection) ? (
                  <div className="relative px-2 py-2">
                    <div className="flex items-center space-x-2 opacity-30 pointer-events-none">
                      {(() => {
                        const processor = workspaceElements.find(e => e.id === selectedProcessorForConnection)
                        const processorData = processor?.data as LEDProcessor
                        const numPorts = typeof processorData?.outputPorts === 'number' ? processorData.outputPorts : 8
                        return Array.from({ length: numPorts }, (_, i) => (
                          <div key={i + 1} className="h-10 w-10 flex items-center justify-center rounded-md border-2 border-dashed border-muted-foreground/30 bg-muted/50">
                            <span className="text-xs text-muted-foreground/50 font-medium">{i + 1}</span>
                          </div>
                        ))
                      })()}
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-md pointer-events-none">
                      <Shield className="h-5 w-5 text-destructive opacity-60" />
                      <span className="text-xs text-destructive font-medium">{t('synoptic.backupProcessorDisabled')}</span>
                      <span className="text-xs text-muted-foreground italic">{t('synoptic.backupPortsNotInteractive')}</span>
                    </div>
                  </div>
                ) : (
                <div className="flex items-center space-x-3 overflow-hidden px-2 py-2">
                  {(() => {
                    const processor = workspaceElements.find(e => e.id === selectedProcessorForConnection)
                    const processorData = processor?.data as LEDProcessor
                    const numPorts = typeof processorData?.outputPorts === 'number' ? processorData.outputPorts : 8
                    return Array.from({ length: numPorts }, (_, i) => {
                      const portNumber = i + 1
                      if (isPortBackup(portNumber, selectedProcessorForConnection)) return null

                      const portColor = getPortColor(portNumber, selectedProcessorForConnection)
                      const isSelected = selectedPort === portNumber

                      // Vérifier si ce port a des panneaux connectés
                      const connectedPanelsCount = panelConnections.filter(
                        conn => conn.processorId === selectedProcessorForConnection && conn.port === portNumber
                      ).length

                      const hasConnectedPanels = connectedPanelsCount > 0

                      // Vérifier si ce port a des backups
                      const portBackups = getBackupsForPort(portNumber, selectedProcessorForConnection)

                      return (
                        <div key={portNumber} className="flex flex-col items-center min-w-[10px] space-y-1">
                          {/* Bouton du port principal */}
                          <Button
                            variant={isSelected ? "default" : "outline"}
                            size="sm"
                            className={`h-12 w-12 flex items-center justify-center p-0 relative ${isSelected ? 'ring-2 ring-offset-0' : ''
                              } cursor-grab active:cursor-grabbing transition-all`}
                            style={{
                              ...(isSelected && {
                                borderColor: portColor,
                                backgroundColor: `${portColor}20`,
                                color: portColor
                              }),
                              ...(hasConnectedPanels && !isSelected && {
                                borderColor: portColor,
                                borderWidth: '2px'
                              }),
                              // Style visuel quand le port est en train d'être draggé (Web et Tauri)
                              ...(draggedPort === portNumber && {
                                opacity: 0.5,
                                transform: 'scale(0.95)',
                                borderColor: portColor,
                                borderWidth: '3px',
                                boxShadow: `0 0 10px ${portColor}`,
                                cursor: 'grabbing'
                              }),
                              // Style visuel quand ce port est la cible du drag
                              ...(portDragTarget === portNumber && {
                                borderColor: portColor,
                                borderWidth: '3px',
                                backgroundColor: `${portColor}30`,
                                boxShadow: `0 0 15px ${portColor}`,
                                transform: 'scale(1.1)'
                              })
                            }}
                            data-onboarding={`port-${portNumber}`}
                            data-port-number={portNumber}
                            onClick={() => {
                              setSelectedPort(selectedPort === portNumber ? null : portNumber)
                              // Notifier l'onboarding de la sélection de port
                              if (onboarding.state.isActive && selectedPort !== portNumber) {
                                onboarding.completeAction('port-selected')
                              }
                            }}
                            onMouseDown={(e) => {
                              if (readOnly) return
                              // Utiliser mousedown pour les deux plateformes (pas de HTML5 drag)
                              if (!isPortPrimary(portNumber, selectedProcessorForConnection)) return
                              const hasBackup = getBackupForPort(portNumber, selectedProcessorForConnection)
                              if (hasBackup) {
                                e.preventDefault()
                                return
                              }
                              setDraggedPort(portNumber)
                              setPortDragMousePosition({ x: e.clientX, y: e.clientY })
                              e.preventDefault()
                            }}
                            title={t('synoptic.portInfo', { port: portNumber, count: connectedPanelsCount })}
                          >
                            <span className="text-xs font-medium">{portNumber}</span>

                            {/* Indicateur du nombre de panneaux */}
                            {hasConnectedPanels && connectedPanelsCount > 1 && (
                              <div className="absolute -top-2 -right-2 w-4 h-3 bg-green-600 rounded-full border border-background flex items-center justify-center">
                                <span className="text-[8px] text-white font-bold">{connectedPanelsCount}</span>
                              </div>
                            )}
                          </Button>

                          {/* Pas de barre colorée pour les ports qui ont des backups */}
                          {portBackups.length === 0 && (
                            <div className="flex items-center space-x-1">
                              <div
                                className="w-8 h-1 rounded-full"
                                style={{ backgroundColor: portColor }}
                              />
                            </div>
                          )}

                          {/* Ports backup en dessous */}
                          {portBackups.map((backupPortNum) => {
                            const backupPortColor = getPortColor(backupPortNum)
                            const backupHasPanels = panelConnections.filter(
                              conn => conn.processorId === selectedProcessorForConnection && conn.port === backupPortNum
                            ).length > 0

                            return (
                              <div key={backupPortNum} className="flex flex-col items-center relative">
                                {/* Icône de lien superposée entre le port principal et le backup */}
                                <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2 z-10">
                                  <LinkIcon className="h-6 w-3 opacity-90" style={{ color: 'white' }} />
                                </div>

                                {/* Bouton du port backup */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-10 w-10 flex items-center justify-center p-0 relative opacity-75 border-dashed"
                                  style={{
                                    borderColor: backupPortColor,
                                    backgroundColor: `${backupPortColor}15`
                                  }}
                                  onClick={() => setSelectedPort(backupPortNum)}
                                  title={t('synoptic.backupPortInfo', { backup: backupPortNum, primary: portNumber })}
                                >
                                  <span className="text-xs font-medium">{backupPortNum}</span>

                                  {/* Indicateur de connexion backup */}
                                  {backupHasPanels && (
                                    <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
                                  )}

                                  {/* Bouton de suppression pour les backups */}
                                  <div
                                    className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 hover:bg-red-600 rounded-full border border-background flex items-center justify-center transition-colors cursor-pointer z-20"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                                      if (selectedProcessorForConnection) {
                                        // Désactiver la sauvegarde automatique
                                        setSkipNextHistorySave(true)

                                        // Générer une description pour l'historique
                                        const description = `Backup du port ${portNumber} supprimé`

                                        setBackupPorts(prev => {
                                          const newBackups = { ...prev }
                                          if (newBackups[selectedProcessorForConnection]) {
                                            const processorBackups = { ...newBackups[selectedProcessorForConnection] }
                                            delete processorBackups[portNumber]
                                            newBackups[selectedProcessorForConnection] = processorBackups
                                          }
                                          return newBackups
                                        })

                                        // Sauvegarder immédiatement avec description
                                        setTimeout(() => {
                                          setSkipNextHistorySave(false)
                                          saveToHistoryImmediate(description)
                                        }, 100)
                                      }
                                    }}
                                    title={t('synoptic.removeBackup')}
                                  >
                                    <span className="text-[8px] text-white font-bold">×</span>
                                  </div>
                                </Button>
                              </div>
                            )
                          })}

                        </div>
                      )
                    })
                  })()}
                </div>
                )}
              </div >

              {/* Color picker pour le port sélectionné */}
              {/* Couleur port déplacé dans le panneau droit */}
            </>
          )}

          {selectedProcessorForConnection && !selectedPort && workspaceElements.find(e => e.id === selectedProcessorForConnection) && (
            <>
              <Separator orientation="vertical" className="h-24" />
              <div className="flex flex-col items-start space-y-3 px-2" data-no-deselect>
                <div className="flex flex-col items-start space-y-1">
                  <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{t('synoptic.processorToPanelLink')}</span>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={showProcessorToFirstPanelLinks}
                      onCheckedChange={(v) => {
                        if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                        setShowProcessorToFirstPanelLinks(v)
                      }}
                    />
                    <span className="text-xs text-muted-foreground">
                      {showProcessorToFirstPanelLinks ? t('synoptic.visible') : t('synoptic.hidden')}
                    </span>
                  </div>
                </div>

                {/* Option Backup Processor */}
                <div className="flex flex-col items-start space-y-1">
                  <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{t('synoptic.setProcessorBackup') || 'Définir processeur backup'}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full cursor-grab active:cursor-grabbing"
                    onMouseDown={(e) => {
                      if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                      if (e.button === 0) { // Clic gauche uniquement
                        e.preventDefault()
                        setProcessorForBackup(selectedProcessorForConnection)
                        setIsDraggingBackupButton(true)
                        setBackupButtonDragMousePosition({ x: e.clientX, y: e.clientY })
                      }
                    }}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    {t('synoptic.iWillBeYourBackup') || 'I will be your backup'}
                  </Button>
                  {(() => {
                    const backupProcId = getBackupProcessor(selectedProcessorForConnection)
                    const primaryProcId = getPrimaryProcessor(selectedProcessorForConnection)
                    if (backupProcId || primaryProcId) {
                      return (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-destructive hover:text-destructive"
                          onClick={() => {
                            if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                            if (backupProcId) {
                              removeBackupProcessor(selectedProcessorForConnection)
                            } else if (primaryProcId) {
                              removeBackupProcessor(primaryProcId)
                            }
                          }}
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          {t('synoptic.removeBackup') || 'Retirer le backup'}
                        </Button>
                      )
                    }
                    return null
                  })()}
                </div>
              </div>
            </>
          )}


          {selectedProcessorForConnection && selectedPort && workspaceElements.find(e => e.id === selectedProcessorForConnection) && (
            <>
              <Separator orientation="vertical" className="h-24" />
              {/* Jauge de progression Daisy Chain */}
              <div className="flex flex-col items-center space-y-2 min-w-[120px]">
                <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">{t('synoptic.daisyChain')}</span>
                <div className="w-full">
                  {(() => {
                    // Si le port sélectionné est un backup, afficher la daisy chain de son port principal
                    const effectivePort = isPortBackup(selectedPort, selectedProcessorForConnection)
                      ? (getPrimaryForBackup(selectedPort, selectedProcessorForConnection) ?? selectedPort)
                      : selectedPort

                    const panelCount = panelConnections.filter(
                      conn => conn.processorId === selectedProcessorForConnection && conn.port === effectivePort
                    ).length
                    const mireCount = mireConnections.filter(
                      conn => conn.processorId === selectedProcessorForConnection && conn.port === effectivePort
                    ).length
                    const connectedPanelsCount = panelCount + mireCount

                    const connectedPanelIds = panelConnections
                      .filter(conn => conn.processorId === selectedProcessorForConnection && conn.port === effectivePort)
                      .map(conn => conn.panelId)
                    
                    const portPixelCapacity = calculatePortCapacity(portCapacityConfig)
                    let maxPanelsAllowed = MAX_DAISY_CHAIN_PANELS
                    let connectedPixelLoad = 0
                    
                    if (connectedPanelIds.length > 0) {
                      const firstPanel = workspaceElements.find(el => el.id === connectedPanelIds[0])
                      if (firstPanel && firstPanel.type === 'panel') {
                        const panelData = firstPanel.data as LEDPanel
                        if (panelData.resolution && panelData.resolution.width > 0 && panelData.resolution.height > 0) {
                          maxPanelsAllowed = calculateMaxPanelsPerPort(panelData.resolution, portCapacityConfig)
                        }
                      }

                      connectedPixelLoad = connectedPanelIds.reduce((total, panelId) => {
                        const panel = workspaceElements.find(el => el.id === panelId)
                        if (!panel || panel.type !== 'panel') return total

                        const panelData = panel.data as LEDPanel
                        const resolution = panelData.resolution
                        if (!resolution || resolution.width <= 0 || resolution.height <= 0) return total

                        return total + resolution.width * resolution.height
                      }, 0)
                    }

                    const percentage = connectedPixelLoad > 0
                      ? Math.min((connectedPixelLoad / portPixelCapacity) * 100, 100)
                      : Math.min((connectedPanelsCount / maxPanelsAllowed) * 100, 100)
                    const portColor = getPortColor(effectivePort, selectedProcessorForConnection)

                    // Couleur de la jauge selon le niveau
                    let gaugeColor = portColor
                    if (percentage >= 90) {
                      gaugeColor = "hsl(0, 84%, 60%)" // Rouge si proche de la limite
                    } else if (percentage >= 70) {
                      gaugeColor = "hsl(45, 93%, 47%)" // Orange si en cours
                    }

                    return (
                      <div className="relative">
                        {/* Barre de fond */}
                        <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                          {/* Barre de progression animée */}
                          <div
                            className="h-full rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                            style={{
                              width: `${percentage}%`,
                              backgroundColor: gaugeColor
                            }}
                          >
                            {/* Animation de brillance */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
                          </div>
                        </div>

                        {/* Texte de progression */}
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-xs text-muted-foreground">
                            {connectedPanelsCount}/{maxPanelsAllowed}
                          </span>
                          <span className="text-xs font-medium" style={{ color: gaugeColor }}>
                            {Math.round(percentage)}%
                          </span>
                        </div>

                        {/* Indicateur de limite atteinte */}
                        {connectedPanelsCount >= maxPanelsAllowed && (
                          <div className="flex items-center justify-center mt-1">
                            <span className="text-xs font-medium text-red-500 animate-pulse">
                              ⚠️ Limite atteinte
                            </span>
                          </div>
                        )}
                      </div>
                    )
                  })()}

                </div>
              </div>

              <Separator orientation="vertical" className="h-24" />
              {/* Sélecteur de couleur du port */}
              <div className="flex flex-col items-center space-y-2 min-w-[120px]" data-no-deselect>
                <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Couleur</span>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={(() => {
                      const portColor = getPortColor(selectedPort, selectedProcessorForConnection)
                      return portColorOverrides[selectedProcessorForConnection]?.[selectedPort] || portColor
                    })()}
                    onChange={(e) => {
                      if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                      setPortColorOverrides(prev => ({
                        ...prev,
                        [selectedProcessorForConnection]: {
                          ...(prev[selectedProcessorForConnection] || {}),
                          [selectedPort]: e.target.value
                        }
                      }))
                    }}
                    className={`w-10 h-10 rounded border p-0.5 bg-background ${readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                    title={`Personnaliser la couleur du port ${selectedPort}`}
                    disabled={readOnly}
                  />
                  {portColorOverrides[selectedProcessorForConnection]?.[selectedPort] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs text-muted-foreground"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                        setPortColorOverrides(prev => {
                          const next = { ...prev }
                          if (next[selectedProcessorForConnection]) {
                            next[selectedProcessorForConnection] = { ...next[selectedProcessorForConnection] }
                            delete next[selectedProcessorForConnection][selectedPort]
                          }
                          return next
                        })
                      }}
                      title={t('title.resetColor')}
                      disabled={readOnly}
                    >
                      ↺ Reset
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Bande de rappel panneau du bas (quand fermé) - Panels */}
      {currentStep === 'data' && !isBottomPanelOpen && (
        <div
          className="h-8 bg-gradient-to-t from-muted/40 to-muted/20 border-t-2 border-blue-500/20 hover:border-blue-500/40 hover:from-muted/60 hover:to-muted/40 cursor-pointer transition-all duration-300 flex flex-row items-center justify-center gap-4 relative group"
          onClick={() => setIsBottomPanelOpen(true)}
          title="Cliquer pour ouvrir le panneau de gestion des panneaux"
        >
          {/* Indicateur visuel d'ouverture */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-blue-500/30 group-hover:bg-blue-500/60 transition-colors"></div>

          <div className="text-xs text-muted-foreground group-hover:text-foreground font-medium whitespace-nowrap transition-colors">
            Processeurs & Ports Management
          </div>

          <div className="flex flex-row gap-2">
            <Cpu className="h-3 w-3 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity" />
            <Monitor className="h-3 w-3 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      )}

      {/* Menu en bas pour Power Management (Armoires) */}
      {currentStep === 'cabling' && isBottomPanelOpen && (
        <div
          className="h-40 bg-background border-t border-border flex items-start px-4 space-x-6 overflow-hidden py-4 relative"
          onClick={(e) => {
            if (readOnly) return
            // Désélectionner la prise si on clique sur le bandeau (mais pas sur les boutons ou éléments interactifs)
            const target = e.target as HTMLElement
            const isClickOnButton = target.closest('button')
            const isClickOnInteractive = target.closest('[data-no-deselect]')

            if (!isClickOnButton && !isClickOnInteractive) {
              setSelectedOutlet(null)
            }
          }}
        >
          {/* Bouton pour masquer le bandeau du bas */}
          <div
            className="absolute top-2 right-4 cursor-pointer hover:bg-muted/50 rounded-md p-1 transition-colors flex items-center gap-1 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation()
              setIsBottomPanelOpen(false)
            }}
            title="Cliquer pour masquer le panneau du bas"
          >
            <ChevronDown className="h-4 w-4" />
            <span className="text-xs">{t('synoptic.hide')}</span>
          </div>
          <div className="flex flex-col items-start space-y-2 min-w-0 overflow-hidden">
            <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">{t('synoptic.electricalCabinets')}</span>
            <div className="flex items-center space-x-2 overflow-hidden">
              {workspaceElements
                .filter(element => element.type === 'cabinet')
                .map(cabinet => (
                  <Button
                    key={cabinet.id}
                    variant={selectedCabinetForConnection === cabinet.id ? "default" : "outline"}
                    size="sm"
                    className="h-20 w-20 flex flex-col items-center justify-center p-1"
                    onClick={() => {
                      setCabinetLinkMode(false)
                      if (selectedCabinetForConnection === cabinet.id) {
                        setSelectedCabinetForConnection(null)
                        setSelectedOutlet(null)
                      } else {
                        setSelectedCabinetForConnection(cabinet.id)
                        setSelectedOutlet(null)
                      }
                    }}
                  >
                    <Database className="h-4 w-4 mb-1 text-red-500" />
                    <span className="text-xs font-medium truncate w-full">{(cabinet.data as PowerCabinet).brand}</span>
                  </Button>
                ))}
              {workspaceElements.filter(element => element.type === 'cabinet').length === 0 && (
                <span className="text-xs text-muted-foreground italic">{t('synoptic.noCabinetPlaced')}</span>
              )}
            </div>
          </div>

          {selectedCabinetForConnection && workspaceElements.find(e => e.id === selectedCabinetForConnection) && (
            <>
              <Separator orientation="vertical" className="h-24" />
              <div className="flex flex-col items-start min-w-0 overflow-hidden">
                <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">{t('synoptic.prisesPC16')}</span>
                <div className="flex items-center space-x-2 overflow-x-auto px-2 py-2">
                  {(() => {
                    const cabinet = workspaceElements.find(e => e.id === selectedCabinetForConnection)
                    const cabinetData = cabinet?.data as PowerCabinet
                    return Array.from({ length: cabinetData?.outlets || 12 }, (_, i) => {
                      const outletNumber = i + 1
                      const isSelected = selectedOutlet === outletNumber

                      // Vérifier si cette prise a des panneaux connectés
                      const connectedPanelsCount = powerConnections.filter(
                        conn => conn.cabinetId === selectedCabinetForConnection && conn.outlet === outletNumber
                      ).length

                      return (
                        <div key={outletNumber} className="flex flex-col items-center min-w-[48px] space-y-1">
                          <Button
                            variant={isSelected ? "default" : "outline"}
                            size="sm"
                            className={`h-12 w-12 flex items-center justify-center p-0 relative ${isSelected ? 'ring-2 ring-offset-0' : ''
                              }`}
                            style={{
                              ...(isSelected && {
                                borderColor: getOutletColor(outletNumber, selectedCabinetForConnection),
                                backgroundColor: `${getOutletColor(outletNumber, selectedCabinetForConnection)}20`,
                                color: getOutletColor(outletNumber, selectedCabinetForConnection),
                                ringColor: getOutletColor(outletNumber, selectedCabinetForConnection)
                              }),
                              ...(connectedPanelsCount > 0 && !isSelected && {
                                borderColor: getOutletColor(outletNumber, selectedCabinetForConnection),
                                borderWidth: '2px'
                              })
                            }}
                            onClick={() => {
                              setCabinetLinkMode(false)
                              setSelectedOutlet(selectedOutlet === outletNumber ? null : outletNumber)
                              if (onboarding.state.isActive && selectedOutlet !== outletNumber) {
                                onboarding.completeAction('outlet-selected')
                              }
                            }}
                            {...(outletNumber === 1 ? { 'data-onboarding': 'outlet-pc16-1' } : {})}
                            title={t('title.pc16Outlet').replace('{number}', String(outletNumber)).replace('{count}', String(connectedPanelsCount)).replace('{plural}', connectedPanelsCount > 1 ? 's' : '')}
                          >
                            <Zap className="h-4 w-4" />
                            {connectedPanelsCount > 0 && (
                              <div
                                className="absolute -top-2 -right-2 w-4 h-3 rounded-full border border-background flex items-center justify-center"
                                style={{ backgroundColor: getOutletColor(outletNumber, selectedCabinetForConnection) }}
                              >
                                <span className="text-[9px] font-bold text-white">{connectedPanelsCount}</span>
                              </div>
                            )}
                          </Button>
                          <span className="text-[10px] text-muted-foreground">{outletNumber}</span>
                        </div>
                      )
                    })
                  })()}
                </div>
              </div>
            </>
          )}

          {/* Couleur prise déplacé dans le panneau droit */}


          {selectedCabinetForConnection && !selectedOutlet && workspaceElements.find(e => e.id === selectedCabinetForConnection) && (
            <>
              <Separator orientation="vertical" className="h-24" />
              <div className="flex flex-col items-start space-y-1 px-2" data-no-deselect>
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">Liaison entre l'armoire et le 1er panel :</span>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={showCabinetToFirstPanelLinks}
                    onCheckedChange={(v) => {
                      if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                      setShowCabinetToFirstPanelLinks(v)
                    }}
                  />
                  <span className="text-xs text-muted-foreground">
                    {showCabinetToFirstPanelLinks ? 'Visible' : 'Masqué'}
                  </span>
                </div>
              </div>

              {/* Charge électrique de l'armoire (liaisons armoire → armoire incluses) */}
              <Separator orientation="vertical" className="h-24" />
              <div className="flex flex-col items-start space-y-1 px-2" data-no-deselect>
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{t('synoptic.cabinetLoad')}</span>
                {(() => {
                  const cab = workspaceElements.find(e => e.id === selectedCabinetForConnection)
                  const maxW = (cab?.data as PowerCabinet)?.maxPower || 0
                  const directW = cabinetLoadDirect.get(selectedCabinetForConnection) || 0
                  const totalW = cabinetLoadTotal.get(selectedCabinetForConnection) || 0
                  const linkedW = totalW - directW
                  const overloaded = maxW > 0 && totalW > maxW
                  return (
                    <>
                      <div className={`text-xs font-semibold ${overloaded ? 'text-red-500' : ''}`}>
                        {(totalW / 1000).toFixed(2)} / {(maxW / 1000).toFixed(1)} kW
                      </div>
                      {linkedW > 0 && (
                        <div className="text-[10px] text-muted-foreground">
                          {t('synoptic.cabinetLoadLinked', { kw: (linkedW / 1000).toFixed(2) })}
                        </div>
                      )}
                      {overloaded && (
                        <div className="text-[10px] font-medium text-red-500 animate-pulse">
                          {t('synoptic.cabinetOverload')}
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>

              {/* Liaison armoire → armoire */}
              <Separator orientation="vertical" className="h-24" />
              <div className="flex flex-col items-start space-y-1 px-2" data-no-deselect>
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{t('synoptic.cabinetToCabinetLink')}</span>
                <Button
                  variant={cabinetLinkMode ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                    setCabinetLinkMode(!cabinetLinkMode)
                    if (!cabinetLinkMode) toast.info(t('synoptic.cabinetLinkPickTarget'))
                  }}
                >
                  <LinkIcon className="h-3 w-3 mr-1" />
                  {cabinetLinkMode ? t('synoptic.cabinetLinkCancel') : t('synoptic.cabinetLinkCreate')}
                </Button>
                {/* Liens existants impliquant l'armoire sélectionnée */}
                {cabinetLinkCables
                  .filter(l => l.from === selectedCabinetForConnection || l.to === selectedCabinetForConnection)
                  .map(link => {
                    const isSource = link.from === selectedCabinetForConnection
                    const otherId = isSource ? link.to : link.from
                    const otherEl = workspaceElements.find(e => e.id === otherId)
                    const otherName = otherEl ? (otherEl.data as PowerCabinet).brand || (otherEl.data as PowerCabinet).name : '?'
                    const loadW = cabinetLoadTotal.get(otherId) || 0
                    return (
                      <div key={link.id} className="flex items-center gap-1 text-[10px]">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: link.color }} />
                        <span className="text-muted-foreground">
                          {isSource
                            ? `${t('synoptic.cabinetLinkFeeds')} ${otherName} (${(loadW / 1000).toFixed(1)} kW)`
                            : `${t('synoptic.cabinetLinkFedBy')} ${otherName}`}
                        </span>
                        {!readOnly && (
                          <button
                            className="w-3.5 h-3.5 bg-red-500 hover:bg-red-600 rounded-full text-white text-[9px] font-bold leading-none flex items-center justify-center"
                            onClick={() => removeCabinetLink(link.id)}
                            title={t('synoptic.removeLink')}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )
                  })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Bande de rappel panneau du bas (quand fermé) - Cabling */}
      {currentStep === 'cabling' && !isBottomPanelOpen && (
        <div
          className="h-8 bg-gradient-to-t from-muted/40 to-muted/20 border-t-2 border-red-500/20 hover:border-red-500/40 hover:from-muted/60 hover:to-muted/40 cursor-pointer transition-all duration-300 flex flex-row items-center justify-center gap-4 relative group"
          onClick={() => setIsBottomPanelOpen(true)}
          title="Cliquer pour ouvrir le panneau de gestion Power"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-red-600 dark:text-red-400">
            <Zap className="h-4 w-4" />
            <span>{t('synoptic.powerManagement')}</span>
          </div>
          <div className="text-xs text-red-500/60 dark:text-red-400/60">
            {t('synoptic.clickToOpen')}
          </div>
        </div>
      )}
    </>
  )
}
